import { useState, useCallback, useEffect } from "react";
import "@/App.css";
import { analysesApi } from "@/lib/api";
import { Toaster, toast } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import LoginPage from "@/pages/LoginPage";
import UserManagement from "@/pages/UserManagement";
import DashboardPage from "@/pages/Dashboard";
import DataImportPage from "@/pages/DataImport";
import DealComparisonPage from "@/pages/DealComparison";
import SDECalculatorPage from "@/pages/SDECalculator";
import PnLSummaryPage from "@/pages/PnLSummary";
import SolvencyPage from "@/pages/Solvency";
import InventoryPage from "@/pages/Inventory";
import DCFValuationPage from "@/pages/DCFValuation";
import ScenarioAnalysisPage from "@/pages/ScenarioAnalysis";
import SensitivityPage from "@/pages/SensitivityPage";
import KeyFindingsPage from "@/pages/KeyFindings";
import ReportGeneratorPage from "@/pages/ReportGenerator";
import SharedViewPage from "@/pages/SharedView";
import WorkingCapitalPage from "@/pages/WorkingCapitalPage";
import ComparisonPage from "@/pages/ComparisonPage";
import UserGuidePage from "@/pages/UserGuide";
import ValidationPage from "@/pages/ValidationPage";

function AppContent() {
  const { isAuthenticated, loading: authLoading, user, isAdmin, isClient, logout } = useAuth();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analyses, setAnalyses] = useState([]);
  const [isSharedView, setIsSharedView] = useState(false);
  
  // Derive user access from the current analysis
  const userAccess = analysis?._user_access || { is_owner: true, access_level: 'full', read_only: false };
  const isReadOnly = userAccess.read_only;

  // Check for shared view on load
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('share')) {
      setIsSharedView(true);
    }
  }, []);

  const loadAnalyses = useCallback(async () => {
    try {
      const res = await analysesApi.list();
      setAnalyses(res.data || []);
      if (res.data?.length > 0 && !analysis) {
        const latest = res.data[0];
        const full = await analysesApi.get(latest.id);
        setAnalysis(full.data);
      }
    } catch (e) {
      console.error("Failed to load analyses:", e);
    }
  }, [analysis]);

  // Only load analyses after authentication is confirmed
  useEffect(() => { 
    if (!isSharedView && isAuthenticated && !authLoading) {
      loadAnalyses(); 
    }
  }, [isSharedView, isAuthenticated, authLoading]);

  const refreshAnalyses = useCallback(async () => {
    try {
      const res = await analysesApi.list();
      setAnalyses(res.data || []);
      // Refresh current analysis too
      if (analysis?.id) {
        const full = await analysesApi.get(analysis.id);
        setAnalysis(full.data);
      }
    } catch (e) {
      console.error("Refresh error:", e);
    }
  }, [analysis]);

  const saveAnalysis = useCallback(async (inputData) => {
    // Prevent saves in read-only mode
    if (isReadOnly) {
      toast.error("This analysis is read-only");
      return null;
    }
    setLoading(true);
    try {
      let id = analysis?.id;
      if (!id) {
        const res = await analysesApi.create({ name: inputData.name || "New Analysis" });
        id = res.data.id;
      }
      const res = await analysesApi.updateData(id, inputData);
      setAnalysis(res.data);
      toast.success("Analysis saved & calculated");
      refreshAnalyses();
      return res.data;
    } catch (e) {
      console.error("Save error:", e);
      // Handle FastAPI validation errors which return an array
      const detail = e.response?.data?.detail;
      let errorMsg = "Failed to save analysis";
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        errorMsg = detail[0]?.msg || "Validation error";
      }
      toast.error(errorMsg);
      return null;
    } finally {
      setLoading(false);
    }
  }, [analysis, refreshAnalyses, isReadOnly]);

  const handleImport = useCallback(async (parsedData) => {
    // Prevent imports in read-only mode
    if (isReadOnly) {
      toast.error("This analysis is read-only");
      return;
    }
    setLoading(true);
    try {
      let id = analysis?.id;
      if (!id) {
        const res = await analysesApi.create({ name: "Imported Analysis" });
        id = res.data.id;
      }
      const inputData = {
        name: parsedData.name || "Imported Analysis",
        description: parsedData.description || "Imported from spreadsheet",
        ...parsedData,
      };
      const res = await analysesApi.updateData(id, inputData);
      setAnalysis(res.data);
      toast.success("Data imported & calculated successfully");
      setActiveTab("deal-comparison");
      refreshAnalyses();
    } catch (e) {
      console.error("Import error:", e);
      toast.error(e.response?.data?.detail || "Failed to import data");
    } finally {
      setLoading(false);
    }
  }, [analysis, refreshAnalyses, isReadOnly]);

  const selectAnalysis = useCallback(async (id) => {
    try {
      const res = await analysesApi.get(id);
      setAnalysis(res.data);
    } catch (e) {
      toast.error("Failed to load analysis");
    }
  }, []);

  const createNew = useCallback(async () => {
    try {
      const res = await analysesApi.create({ name: "New Analysis" });
      setAnalysis(res.data);
      setActiveTab("import");
      toast.success("New analysis created");
      refreshAnalyses();
    } catch (e) {
      toast.error("Failed to create analysis");
    }
  }, [refreshAnalyses]);

  const recalculate = useCallback(async () => {
    if (!analysis?.id) return;
    setLoading(true);
    try {
      const res = await analysesApi.recalculate(analysis.id);
      setAnalysis(res.data);
      toast.success("Recalculation complete");
      refreshAnalyses();
    } catch (e) {
      console.error("Recalculate error:", e);
      toast.error("Failed to recalculate");
    } finally {
      setLoading(false);
    }
  }, [analysis, refreshAnalyses]);

  // Shared view
  if (isSharedView) {
    return (
      <div className="App dark min-h-screen bg-[#09090b]">
        <div className="max-w-5xl mx-auto p-6">
          <SharedViewPage />
        </div>
        <Toaster theme="dark" position="top-right" />
      </div>
    );
  }

  const data = analysis?.data || {};
  const results = analysis?.results || {};

  const renderPage = () => {
    const commonProps = { data, results, analysis, loading, saveAnalysis };

    switch (activeTab) {
      case "dashboard":
        return <DashboardPage {...commonProps} analyses={analyses} onSelect={selectAnalysis} onNew={createNew} onNavigate={setActiveTab} onRefresh={refreshAnalyses} />;
      case "import":
        return <DataImportPage onImport={handleImport} loading={loading} existingData={data} analysisName={analysis?.name} />;
      case "deal-comparison":
        return <DealComparisonPage {...commonProps} />;
      case "sde":
        return <SDECalculatorPage {...commonProps} />;
      case "pnl":
        return <PnLSummaryPage {...commonProps} />;
      case "solvency":
        return <SolvencyPage {...commonProps} />;
      case "inventory":
        return <InventoryPage {...commonProps} />;
      case "working-capital":
        return <WorkingCapitalPage {...commonProps} />;
      case "dcf":
        return <DCFValuationPage {...commonProps} />;
      case "scenarios":
        return <ScenarioAnalysisPage {...commonProps} />;
      case "sensitivity":
        return <SensitivityPage analysis={analysis} results={results} data={data} saveAnalysis={saveAnalysis} onRefresh={refreshAnalyses} />;
      case "findings":
        return <KeyFindingsPage analysis={analysis} results={results} />;
      case "compare":
        return <ComparisonPage analyses={analyses} analysis={analysis} data={data} saveAnalysis={saveAnalysis} loading={loading} />;
      case "validation":
        return <ValidationPage data={data} results={results} analysis={analysis} onRecalculate={recalculate} loading={loading} />;
      case "report":
        return <ReportGeneratorPage analysis={analysis} results={results} data={data} />;
      case "guide":
        return <UserGuidePage />;
      case "users":
        return <UserManagement />;
      default:
        return <DashboardPage {...commonProps} analyses={analyses} onSelect={selectAnalysis} onNew={createNew} onNavigate={setActiveTab} onRefresh={refreshAnalyses} />;
    }
  };

  // Show login if not authenticated (and not a shared view)
  if (!isSharedView && !authLoading && !isAuthenticated) {
    return (
      <>
        <LoginPage />
        <Toaster theme="dark" position="top-right" />
      </>
    );
  }

  // Client users have read-only access
  const readOnly = isReadOnly;

  return (
    <div className="App dark">
      <Layout 
        activeTab={activeTab} 
        onTabChange={setActiveTab} 
        analysisName={analysis?.name}
        user={user}
        onLogout={logout}
        isAdmin={isAdmin}
        userAccess={userAccess}
        onGoHome={() => {
          setAnalysis(null);
          setActiveTab('dashboard');
        }}
      >
        {renderPage()}
      </Layout>
      <Toaster theme="dark" position="top-right" />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
